import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiMessageCircle, FiShare2, FiTrash2, FiCamera, FiFolder, FiChevronLeft, FiChevronRight, FiUsers, FiX, FiDownload } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';
import { apiFetch } from '../utils/api';
import { showToast } from './NotificationTray';
import { useAuth } from '../contexts/AuthContext';
import { buildVariantUrl } from '../utils/photoCache';
import { getFeatureFlags } from '../utils/featureFlags';
import PageHelp from './PageHelp';
import '../styles-strip.css';

export default function TheStrip() {
  const { user } = useAuth();
  const features = getFeatureFlags(user);
  const stripEnabled = features.strip;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const requestGeneration = React.useRef(0);
  const loadingRef = React.useRef(false);
  const [commentInputs, setCommentInputs] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [myPhotos, setMyPhotos] = useState([]);
  const [myAlbums, setMyAlbums] = useState([]);
  const [currentAlbum, setCurrentAlbum] = useState(null); // null = top level, object = inside album
  const [albumPhotos, setAlbumPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectedAlbums, setSelectedAlbums] = useState([]);
  const [shareCaption, setShareCaption] = useState('');
  const [sharing, setSharing] = useState(false);
  const [loadingAlbum, setLoadingAlbum] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [expandedAlbum, setExpandedAlbum] = useState(null); // { post, currentIndex }
  const getStripVariant = useCallback((image, variant = 'xl') => {
    if (!image) return null;
    return buildVariantUrl(image, variant);
  }, []);
  const getOriginalUrl = useCallback((image, download = false) => {
    if (!image?.filename) return null;
    return `/media/originals/${encodeURIComponent(image.filename)}${download ? '?download=1' : ''}`;
  }, []);
  const profileName = user?.display_name || user?.username || user?.email || 'You';
  const profileHandle = user?.username || (user?.email ? user.email.split('@')[0] : 'you');
  const profileInitial = (profileName?.[0] || 'Y').toUpperCase();
  const profilePhotoUrl = user?.profile_photo_url || null;
  const totalPhotos = posts.reduce((sum, post) => {
    if (post.post_type === 'album') {
      return sum + (post.album_images?.length || 0);
    }
    return sum + 1;
  }, 0);
  const totalLikes = posts.reduce((sum, post) => sum + (post.like_count || 0), 0);
  const getLikeLabel = useCallback((liker) => {
    if (!liker) return 'someone';
    if (user?.id && String(liker.user_id) === String(user.id)) return 'you';
    const handle = liker.username;
    return handle ? `@${handle}` : 'someone';
  }, [user]);

  const buildLikeSummary = useCallback((likes = []) => {
    if (!likes.length) return null;
    const sortedLikes = [...likes];
    if (user?.id) {
      sortedLikes.sort((a, b) => {
        const aIsMe = String(a.user_id) === String(user.id);
        const bIsMe = String(b.user_id) === String(user.id);
        if (aIsMe === bIsMe) return 0;
        return aIsMe ? -1 : 1;
      });
    }
    const maxNames = 3;
    const displayed = sortedLikes.slice(0, maxNames).map(getLikeLabel);
    const remaining = likes.length - displayed.length;
    return { displayed, remaining };
  }, [getLikeLabel, user]);

  const openLightbox = (post, startIndex = 0) => {
    const isAlbum = post.post_type === 'album';
    const images = isAlbum
      ? (post.album_images || [])
      : (post.filename ? [{ filename: post.filename }] : []);
    if (!images.length) return;
    setExpandedAlbum({
      post: {
        ...post,
        album_images: images
      },
      currentIndex: startIndex,
      isAlbum
    });
  };

  const loadFeed = useCallback(async (reset = false, nextOffset = 0) => {
    if (loadingRef.current && !reset) return;
    const generation = ++requestGeneration.current;
    loadingRef.current = true;
    try {
      setLoading(true);
      const newOffset = reset ? 0 : nextOffset;
      const res = await apiFetch(`/api/strip/feed?limit=20&offset=${newOffset}`);
      if (!res.ok) throw new Error('Feed request failed');
      const response = await res.json();
      if (generation !== requestGeneration.current) return;

      if (reset) {
        setPosts(response.posts || []);
      } else {
        setPosts(prev => [...prev, ...(response.posts || [])]);
      }

      setHasMore(response.hasMore);
      setOffset(newOffset + (response.posts || []).length);
    } catch (error) {
      console.error('Failed to load feed:', error);
      showToast('Failed to load The Strip', 'error');
    } finally {
      if (generation === requestGeneration.current) { setLoading(false); loadingRef.current = false; }
    }
  }, []);

  useEffect(() => {
    setPosts([]);
    loadFeed(true);
    return () => { requestGeneration.current++; loadingRef.current = false; };
  }, [stripEnabled, loadFeed]);

  // Keyboard navigation for album lightbox
  useEffect(() => {
    if (!expandedAlbum) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setExpandedAlbum(null);
      } else if (e.key === 'ArrowLeft') {
        setExpandedAlbum(prev => ({
          ...prev,
          currentIndex: (prev.currentIndex - 1 + prev.post.album_images.length) % prev.post.album_images.length
        }));
      } else if (e.key === 'ArrowRight') {
        setExpandedAlbum(prev => ({
          ...prev,
          currentIndex: (prev.currentIndex + 1) % prev.post.album_images.length
        }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedAlbum]);

  const handleLike = async (postId, postType = 'photo') => {
    try {
      const endpoint = postType === 'album'
        ? `/api/strip/album-posts/${postId}/like`
        : `/api/strip/posts/${postId}/like`;

      const res = await apiFetch(endpoint, { method: 'POST' });
      if (!res.ok) throw new Error('Update failed');
      const response = await res.json();

      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const currentLikes = post.likes || [];
          const currentUserId = user?.id;
          let nextLikes = currentLikes;
          if (currentUserId) {
            if (response.liked) {
              if (!currentLikes.some(like => String(like.user_id) === String(currentUserId))) {
                nextLikes = [{
                  user_id: currentUserId,
                  username: user?.username || null,
                  profile_photo_key: user?.profile_photo_key || null
                }, ...currentLikes];
              }
            } else {
              nextLikes = currentLikes.filter(like => String(like.user_id) !== String(currentUserId));
            }
          }
          return {
            ...post,
            is_liked: response.liked,
            like_count: response.liked ? (post.like_count || 0) + 1 : Math.max(0, (post.like_count || 0) - 1),
            likes: nextLikes
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Failed to toggle like:', error);
      showToast('Failed to update like', 'error');
    }
  };

  const handleComment = async (postId, postType = 'photo') => {
    const comment = commentInputs[postId]?.trim();
    if (!comment) return;

    try {
      const endpoint = postType === 'album'
        ? `/api/strip/album-posts/${postId}/comment`
        : `/api/strip/posts/${postId}/comment`;

      const res = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ comment })
      });
      if (!res.ok) throw new Error('Update failed');
      const response = await res.json();

      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            comments: [...(post.comments || []), response],
            comment_count: post.comment_count + 1
          };
        }
        return post;
      }));

      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
    } catch (error) {
      console.error('Failed to add comment:', error);
      showToast('Failed to add comment', 'error');
    }
  };

  const handleDeleteComment = async (commentId, postId, postType = 'photo') => {
    try {
      const endpoint = postType === 'album'
        ? `/api/strip/album-comments/${commentId}`
        : `/api/strip/comments/${commentId}`;

      const res = await apiFetch(endpoint, { method: 'DELETE' });
      if (!res.ok) throw new Error('Removal failed');

      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            comments: post.comments.filter(c => c.id !== commentId),
            comment_count: post.comment_count - 1
          };
        }
        return post;
      }));

      showToast('Comment deleted', 'success');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      showToast('Failed to delete comment', 'error');
    }
  };

  const handleDeletePost = async (postId, postType = 'photo') => {
    const confirmMsg = postType === 'album' ? 'Remove this album from The Strip?' : 'Remove this photo from The Strip?';
    if (!confirm(confirmMsg)) return;

    try {
      const endpoint = postType === 'album'
        ? `/api/strip/album-posts/${postId}`
        : `/api/strip/posts/${postId}`;

      const res = await apiFetch(endpoint, { method: 'DELETE' });
      if (!res.ok) throw new Error('Removal failed');

      setPosts(prev => prev.filter(p => p.id !== postId));
      showToast('Removed from The Strip', 'success');
    } catch (error) {
      console.error('Failed to delete post:', error);
      showToast('Failed to remove post', 'error');
    }
  };

  const loadMyPhotos = async () => {
    setLoadingPhotos(true);
    try {
      // Load both photos and albums in parallel
      const [photosRes, albumsRes] = await Promise.all([
        apiFetch('/api/images?limit=100&fields=compact&variants=xl,lg'),
        apiFetch('/api/albums')
      ]);
      const photosData = await photosRes.json();
      const albumsData = await albumsRes.json();
      setMyPhotos(photosData.images || []);
      setMyAlbums(albumsData.albums || albumsData || []);
    } catch (error) {
      console.error('Failed to load photos:', error);
      showToast('Failed to load your photos', 'error');
    } finally {
      setLoadingPhotos(false);
    }
  };

  const loadAlbumPhotos = async (album) => {
    setLoadingAlbum(true);
    try {
      const res = await apiFetch(`/api/albums/${album.id}`);
      const data = await res.json();
      setAlbumPhotos(data.images || []);
      setCurrentAlbum(album);
    } catch (error) {
      console.error('Failed to load album:', error);
      showToast('Failed to load album', 'error');
    } finally {
      setLoadingAlbum(false);
    }
  };

  const navigateBackFromAlbum = () => {
    setCurrentAlbum(null);
    setAlbumPhotos([]);
  };

  const openShareModal = () => {
    loadMyPhotos();
    setCurrentAlbum(null);
    setAlbumPhotos([]);
    setSelectedPhotos([]);
    setSelectedAlbums([]);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setCurrentAlbum(null);
    setAlbumPhotos([]);
    setSelectedPhotos([]);
    setSelectedAlbums([]);
    setShareCaption('');
  };

  const toggleAlbumSelection = (album, e) => {
    e.stopPropagation();
    setSelectedAlbums(prev => {
      const isSelected = prev.some(a => a.id === album.id);
      if (isSelected) {
        return prev.filter(a => a.id !== album.id);
      } else {
        return [...prev, album];
      }
    });
  };

  const getAlbumCoverUrl = (album) => {
    // Try various cover image sources
    if (album.cover_url) return album.cover_url;
    if (album.cover_image?.filename) {
      return getStripVariant(album.cover_image, 'sm');
    }
    if (album.cover_filename) {
      return getStripVariant({ filename: album.cover_filename }, 'sm');
    }
    // Fallback to first image if available
    if (album.images?.[0]) {
      return getStripVariant(album.images[0], 'sm');
    }
    return null;
  };

  const togglePhotoSelection = (photo) => {
    setSelectedPhotos(prev => {
      const isSelected = prev.some(p => p.id === photo.id);
      if (isSelected) {
        return prev.filter(p => p.id !== photo.id);
      } else {
        return [...prev, photo];
      }
    });
  };

  const handleShare = async () => {
    if (selectedPhotos.length === 0 && selectedAlbums.length === 0) return;
    if (sharing) return;

    setSharing(true);
    try {
      let photoSuccessCount = 0;
      let albumSuccessCount = 0;
      let alreadySharedCount = 0;

      // Share each selected photo
      for (const photo of selectedPhotos) {
        try {
          await apiFetch('/api/strip/share', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              image_id: photo.id,
              caption: selectedPhotos.length === 1 && selectedAlbums.length === 0 ? (shareCaption || null) : null
            })
          });
          photoSuccessCount++;
        } catch (error) {
          if (error.message?.includes('already shared')) {
            alreadySharedCount++;
          }
        }
      }

      // Share each selected album
      for (const album of selectedAlbums) {
        try {
          await apiFetch('/api/strip/share-album', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              album_id: album.id,
              caption: selectedAlbums.length === 1 && selectedPhotos.length === 0 ? (shareCaption || null) : null
            })
          });
          albumSuccessCount++;
        } catch (error) {
          if (error.message?.includes('already shared')) {
            alreadySharedCount++;
          }
        }
      }

      if (photoSuccessCount > 0 || albumSuccessCount > 0) {
        const parts = [];
        if (photoSuccessCount > 0) parts.push(`${photoSuccessCount} photo${photoSuccessCount > 1 ? 's' : ''}`);
        if (albumSuccessCount > 0) parts.push(`${albumSuccessCount} album${albumSuccessCount > 1 ? 's' : ''}`);
        showToast(`Shared ${parts.join(' and ')} to The Strip!`, 'success');
      }
      if (alreadySharedCount > 0) {
        showToast(`${alreadySharedCount} item${alreadySharedCount > 1 ? 's were' : ' was'} already shared`, 'warning');
      }

      closeShareModal();
      loadFeed(true);
    } catch (error) {
      showToast('Failed to share', 'error');
    } finally {
      setSharing(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const getPreviewImage = (post) => {
    if (post.post_type === 'album' && post.album_images?.length) {
      return getStripVariant(post.album_images[0], 'xl');
    }
    if (post.filename) {
      return getStripVariant(post, 'xl');
    }
    return null;
  };

  const heroCards = posts
    .map(post => {
      const image = getPreviewImage(post);
      if (!image) return null;
      return {
        id: post.id,
        image,
        username: post.username || 'friend',
        isAlbum: post.post_type === 'album'
      };
    })
    .filter(Boolean)
    .slice(0, 3);
  const heroCardsClass = heroCards.length === 1
    ? 'strip-hero-cards single'
    : heroCards.length === 2
      ? 'strip-hero-cards double'
      : 'strip-hero-cards';

  const header = (
    <div className="strip-header">
      <div className="strip-title">
        <h1>
          <FiCamera className="inline-icon" />
          The Strip
        </h1>
        <p className="strip-subtitle">Photos from your friends</p>
      </div>
      <button
        className="share-to-strip-btn"
        onClick={openShareModal}
        type="button"
      >
        <FiShare2 />
        <span>Share Photo</span>
      </button>
    </div>
  );

  const helpTip = (
    <div className="page-toolbar">
      <PageHelp
        title="How to use The Strip"
        description="The Strip is your private social feed for sharing moments with your circle."
        tips={[
          'Share photos or albums to start a thread.',
          'Like and comment to keep conversations together.',
          'Use Friends to control who can see your feed.'
        ]}
        subject="Photo Harbor Help - The Strip"
      />
    </div>
  );

  const desktopHero = (
    <section className="strip-desktop-hero">
      <div className="strip-hero-left">
        <div className="strip-hero-kicker">The Strip</div>
        <h1 className="strip-hero-title">A living gallery of your circle.</h1>
        <p className="strip-hero-copy">
          Drop a photo, spark a thread, and watch today's moments unfold in real time.
        </p>
        <div className="strip-hero-actions">
          <button className="strip-hero-btn primary" onClick={openShareModal} type="button">
            <FiShare2 />
            Share a photo
          </button>
          <Link className="strip-hero-btn" to="/friends">
            <FiUsers />
            Find friends
          </Link>
        </div>
        <div className="strip-hero-metrics">
          <div className="strip-metric">
            <div className="strip-metric-value">{posts.length}</div>
            <div className="strip-metric-label">drops today</div>
          </div>
          <div className="strip-metric">
            <div className="strip-metric-value">{totalPhotos}</div>
            <div className="strip-metric-label">photos shared</div>
          </div>
          <div className="strip-metric">
            <div className="strip-metric-value">{totalLikes}</div>
            <div className="strip-metric-label">hearts sent</div>
          </div>
        </div>
      </div>
      <div className="strip-hero-right">
        <div className="strip-hero-profile">
        <div
          className={`strip-hero-avatar ${profilePhotoUrl ? 'has-photo' : ''}`}
          data-initial={profileInitial}
          style={profilePhotoUrl ? { backgroundImage: `url(${profilePhotoUrl})` } : undefined}
        />
          <div>
            <div className="strip-hero-name">{profileName}</div>
            <div className="strip-hero-handle">@{profileHandle}</div>
          </div>
        </div>
        <div className={heroCardsClass}>
          {heroCards.length > 0 ? (
            heroCards.map((card, index) => (
              <div key={card.id} className={`strip-hero-card card-${index + 1}`}>
                <img src={card.image} alt={card.isAlbum ? 'Album preview' : 'Shared photo'} />
                <div className="strip-hero-card-meta">
                  <span>@{card.username}</span>
                  {card.isAlbum && <span className="strip-hero-chip">Album</span>}
                </div>
              </div>
            ))
          ) : (
            <div className="strip-hero-card placeholder">
              <div className="strip-hero-placeholder">
                <FiCamera size={28} />
                <span>Waiting for the first drop</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  // Skeleton post component
  const SkeletonPost = ({ featured = false }) => (
    <div className={`strip-post skeleton-post ${featured ? 'featured' : ''}`}>
      <div className="post-header">
        <div className="post-user">
          <div className="user-avatar skeleton-avatar" />
          <div className="user-info">
            <div className="skeleton-text skeleton-username" />
            <div className="skeleton-text skeleton-timestamp" />
          </div>
        </div>
      </div>
      <div className="strip-post-body">
        <div className="strip-post-media">
          <div className="skeleton-image" />
        </div>
        <div className="strip-post-details">
          <div className="skeleton-text skeleton-caption" />
          <div className="post-actions">
            <div className="skeleton-text skeleton-action" />
            <div className="skeleton-text skeleton-action" />
          </div>
        </div>
      </div>
    </div>
  );

  if (!stripEnabled) {
    return <div className="strip-feed-container">
      <h2>The Strip is off</h2>
      <p>Your posts are hidden from friends. You can remove them below. Turning The Strip back on makes remaining posts visible to your friends again.</p>
      <Link to="/settings">Open settings</Link>
      {posts.map(post => <div key={`${post.post_type}:${post.id}`}>
        <span>{post.album_title || post.caption || 'Shared photo'}</span>
        <button onClick={() => handleDeletePost(post.id, post.post_type)}>Remove from The Strip</button>
      </div>)}
      {hasMore && <button disabled={loading} onClick={() => loadFeed(false, offset)}>Load more</button>}
    </div>;
  }

  if (loading && posts.length === 0) {
    return (
      <div className="strip-container">
        <div className="strip-stage">
          {desktopHero}
          {header}
          {helpTip}
          <div className="strip-feed">
            <SkeletonPost featured />
            <SkeletonPost />
            <SkeletonPost />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="strip-container">
      <div className="strip-stage">
        {desktopHero}
        {header}
        {helpTip}

        {posts.length === 0 ? (
          <div className="empty-feed">
            <FiCamera size={48} />
            <h2>No photos yet</h2>
            <p>Add some friends and they'll share photos here!</p>
          </div>
        ) : (
          <div className="strip-feed">
            {posts.map((post, index) => {
              const isAlbum = post.post_type === 'album';
              const albumImages = post.album_images || [];
              const displayUsername = post.username || 'friend';
              const isFeatured = index === 0;
              const avatarUrl = post.profile_photo_key ? `/api/profile/photo/${post.user_id}` : null;

              return (
                <div
                  key={post.id}
                  className={`strip-post ${isAlbum ? 'album-post' : ''} ${isFeatured ? 'featured' : ''}`}
                >
                  <div className="post-header">
                    <div className="post-user">
                      <div
                        className={`user-avatar ${avatarUrl ? 'has-photo' : ''}`}
                        data-initial={(displayUsername[0] || 'U').toUpperCase()}
                        style={avatarUrl ? { backgroundImage: `url(${avatarUrl})` } : undefined}
                      />
                      <div className="user-info">
                        <span className="username">@{displayUsername}</span>
                        <span className="timestamp">{formatTime(post.created_at)}</span>
                      </div>
                    </div>
                    {post.user_id === user?.id && (
                      <button
                        className="post-menu"
                        onClick={() => handleDeletePost(post.id, post.post_type)}
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>

                  <div className="strip-post-body">
                    <div className="strip-post-media">
                      {/* Post image(s) */}
                      <div
                        className={`post-image ${!isAlbum ? 'is-clickable' : ''}`}
                        onClick={!isAlbum ? () => openLightbox(post, 0) : undefined}
                      >
                        {isAlbum && albumImages.length > 0 ? (
                          <div
                            className="album-collage"
                            onClick={() => openLightbox(post, 0)}
                            data-count={Math.min(albumImages.length, 4)}
                          >
                            {/* Primary large image */}
                            <div className="collage-primary">
                              <img
                                src={getStripVariant(albumImages[0], 'xl')}
                                alt={post.album_title || 'Album photo'}
                                loading="lazy"
                                decoding="async"
                              />
                            </div>
                            {/* Thumbnail strip */}
                            {albumImages.length > 1 && (
                              <div className="collage-thumbs">
                                {albumImages.slice(1, 4).map((img, idx) => (
                                  <div key={img.id || idx} className="collage-thumb">
                                    <img
                                      src={getStripVariant(img, 'md')}
                                      alt=""
                                      loading="lazy"
                                      decoding="async"
                                    />
                                    {idx === 2 && albumImages.length > 4 && (
                                      <div className="collage-more">
                                        +{albumImages.length - 4}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="collage-click-hint">
                              <span>View {albumImages.length} photos</span>
                            </div>
                          </div>
                        ) : post.filename ? (
                          <img
                            src={getStripVariant(post, 'xl')}
                            alt={post.caption || 'Shared photo'}
                            loading="lazy"
                            decoding="async"
                            className="strip-photo"
                            onClick={(e) => {
                              e.stopPropagation();
                              openLightbox(post, 0);
                            }}
                          />
                        ) : null}
                      </div>
                    </div>

                    <div className="strip-post-details">
                      {/* Album title badge with Friends Only indicator */}
                      {isAlbum && post.album_title && (
                        <div className="album-badge">
                          <FiFolder size={14} />
                          <span>{post.album_title}</span>
                          <span className="album-count">{albumImages.length} photos</span>
                          <span className="friends-only-badge">
                            <FiUsers size={12} />
                            Friends Only
                          </span>
                        </div>
                      )}

                      {/* Single photo Friends Only indicator */}
                      {!isAlbum && (
                        <div className="post-privacy-indicator">
                          <FiUsers size={12} />
                          <span>Friends Only</span>
                        </div>
                      )}

                      {post.caption && (
                        <div className="post-caption">
                          <span className="caption-username">@{displayUsername}</span>
                          {post.caption}
                        </div>
                      )}

                      <div className="post-actions">
                        <button
                          className={`action-btn ${post.is_liked ? 'liked' : ''}`}
                          onClick={() => handleLike(post.id, post.post_type)}
                        >
                          {post.is_liked ? <FaHeart /> : <FiHeart />}
                          <span>{post.like_count || 0}</span>
                        </button>
                        <button className="action-btn">
                          <FiMessageCircle />
                          <span>{post.comment_count || 0}</span>
                        </button>
                      </div>

                      {post.likes && post.likes.length > 0 && (() => {
                        const summary = buildLikeSummary(post.likes);
                        if (!summary) return null;
                        return (
                          <div className="post-likes">
                            <span className="like-label">Liked by</span>
                            <span className="like-users">
                              {summary.displayed.join(', ')}
                              {summary.remaining > 0 ? ` and ${summary.remaining} other${summary.remaining > 1 ? 's' : ''}` : ''}
                            </span>
                          </div>
                        );
                      })()}

                      {post.comments && post.comments.length > 0 && (
                        <div className="post-comments">
                          {post.comments.map(comment => (
                            <div key={comment.id} className="comment">
                              <span className="comment-user">@{comment.username}</span>
                              <span className="comment-text">{comment.comment}</span>
                              {comment.user_id === user?.id && (
                                <button
                                  className="delete-comment"
                                  onClick={() => handleDeleteComment(comment.id, post.id, post.post_type)}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="comment-input">
                        <input
                          type="text"
                          placeholder="Add a comment..."
                          value={commentInputs[post.id] || ''}
                          onChange={(e) => setCommentInputs(prev => ({
                            ...prev,
                            [post.id]: e.target.value
                          }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleComment(post.id, post.post_type);
                            }
                          }}
                        />
                        <button
                          onClick={() => handleComment(post.id, post.post_type)}
                          disabled={!commentInputs[post.id]?.trim()}
                        >
                          Post
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {hasMore && (
              <button
                className="load-more-btn"
                onClick={() => loadFeed(false, offset)}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="modal-overlay" onClick={closeShareModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              {currentAlbum ? (
                <button className="back-btn" onClick={navigateBackFromAlbum}>
                  <FiChevronLeft size={20} />
                  <span>Back</span>
                </button>
              ) : (
                <span className="selection-count">{selectedPhotos.length} selected</span>
              )}
              <h2>{currentAlbum ? currentAlbum.title : 'Share to The Strip'}</h2>
              <button className="close-btn" onClick={closeShareModal}>×</button>
            </div>

            {loadingPhotos || loadingAlbum ? (
              <div className="photo-grid skeleton-grid">
                {[...Array(12)].map((_, i) => (
                  <div key={i} className="photo-option skeleton-photo">
                    <div className="skeleton-image" />
                  </div>
                ))}
              </div>
            ) : currentAlbum ? (
              /* Inside an album - show album photos */
              <div className="photo-grid">
                {albumPhotos.length === 0 ? (
                  <div className="empty-message">No photos in this album</div>
                ) : (
                  albumPhotos.map(photo => {
                    const isSelected = selectedPhotos.some(p => p.id === photo.id);
                    return (
                      <div
                        key={photo.id}
                        className={`photo-option ${isSelected ? 'selected' : ''}`}
                        onClick={() => togglePhotoSelection(photo)}
                      >
                        <img
                          src={getStripVariant(photo, 'sm') || photo.thumbnail_url || ''}
                          alt={photo.filename}
                        />
                        <div className="photo-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              /* Top level - show albums then photos */
              <div className="photo-grid">
                {/* Albums section */}
                {myAlbums.map(album => {
                  const isAlbumSelected = selectedAlbums.some(a => a.id === album.id);
                  const coverUrl = getAlbumCoverUrl(album);
                  return (
                    <div
                      key={`album-${album.id}`}
                      className={`photo-option album-option ${isAlbumSelected ? 'selected' : ''}`}
                      onClick={() => loadAlbumPhotos(album)}
                    >
                      {coverUrl ? (
                        <img src={coverUrl} alt={album.title} />
                      ) : (
                        <div className="album-placeholder">
                          <FiFolder size={32} />
                        </div>
                      )}
                      <div className="album-overlay">
                        <FiFolder size={16} />
                        <span className="album-title">{album.title}</span>
                        <span className="album-count">{album.image_count || 0} photos</span>
                      </div>
                      <div className="photo-checkbox album-checkbox">
                        <input
                          type="checkbox"
                          checked={isAlbumSelected}
                          onChange={() => {}}
                          onClick={(e) => toggleAlbumSelection(album, e)}
                        />
                      </div>
                    </div>
                  );
                })}
                {/* Photos section */}
                {myPhotos.map(photo => {
                  const isSelected = selectedPhotos.some(p => p.id === photo.id);
                  return (
                    <div
                      key={photo.id}
                      className={`photo-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => togglePhotoSelection(photo)}
                    >
                      <img
                        src={getStripVariant(photo, 'sm') || photo.thumbnail_url || ''}
                        alt={photo.filename}
                      />
                      <div className="photo-checkbox">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {(selectedPhotos.length === 1 && selectedAlbums.length === 0) || (selectedAlbums.length === 1 && selectedPhotos.length === 0) ? (
              <textarea
                placeholder="Add a caption (optional)..."
                value={shareCaption}
                onChange={(e) => setShareCaption(e.target.value)}
                rows="3"
              />
            ) : null}

            {/* Help text for album selection */}
            {selectedAlbums.length > 0 && !currentAlbum && (
              <div className="share-help-text">
                <p>
                  <strong>{selectedAlbums.length} album{selectedAlbums.length > 1 ? 's' : ''} selected</strong> , 
                  Current and future photos in {selectedAlbums.length > 1 ? 'these albums' : 'this album'} will be shared as {selectedAlbums.length > 1 ? 'album posts' : 'an album post'}.
                </p>
                <p className="help-hint">
                  Tip: Click an album to browse inside and select individual photos instead.
                </p>
              </div>
            )}

            <div className="modal-actions">
              <button onClick={closeShareModal}>Cancel</button>
              <button
                className="primary-btn"
                onClick={handleShare}
                disabled={sharing || (selectedPhotos.length === 0 && selectedAlbums.length === 0)}
              >
                {sharing ? 'Sharing...' : (() => {
                  const parts = [];
                  if (selectedPhotos.length > 0) parts.push(`${selectedPhotos.length} Photo${selectedPhotos.length > 1 ? 's' : ''}`);
                  if (selectedAlbums.length > 0) parts.push(`${selectedAlbums.length} Album${selectedAlbums.length > 1 ? 's' : ''}`);
                  return parts.length > 0 ? `Share ${parts.join(' + ')}` : 'Share';
                })()}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Lightbox */}
      {expandedAlbum && (
        <div className="lightbox" onClick={() => setExpandedAlbum(null)}>
          <button className="lightbox-close-floating" onClick={() => setExpandedAlbum(null)}>
            <FiX size={24} />
          </button>

          <div className="lightbox-single" onClick={e => e.stopPropagation()}>
            <div className="lightbox-image-container">
              <img
                className="lightbox-image"
                src={getStripVariant(expandedAlbum.post.album_images[expandedAlbum.currentIndex], 'xl')}
                alt={expandedAlbum.post.album_title || 'Album photo'}
              />
            </div>
          </div>

          <div className="lightbox-dock" onClick={e => e.stopPropagation()}>
            <div className="dock-group">
              <button
                className="dock-btn"
                onClick={() => setExpandedAlbum(prev => ({
                  ...prev,
                  currentIndex: (prev.currentIndex - 1 + prev.post.album_images.length) % prev.post.album_images.length
                }))}
                disabled={expandedAlbum.post.album_images.length <= 1}
                title="Previous"
              >
                <FiChevronLeft size={22} />
              </button>
              <span className="dock-counter">
                {expandedAlbum.currentIndex + 1} / {expandedAlbum.post.album_images.length}
              </span>
              <button
                className="dock-btn"
                onClick={() => setExpandedAlbum(prev => ({
                  ...prev,
                  currentIndex: (prev.currentIndex + 1) % prev.post.album_images.length
                }))}
                disabled={expandedAlbum.post.album_images.length <= 1}
                title="Next"
              >
                <FiChevronRight size={22} />
              </button>
            </div>

            <div className="dock-divider" />

            <div className="dock-group">
              <a
                className="dock-btn"
                href={getOriginalUrl(expandedAlbum.post.album_images[expandedAlbum.currentIndex], true)}
                download
                title="Download original"
              >
                <FiDownload size={20} />
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
